import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { submitSmsOptIn, getSmsOptInStatus } from '../api/client';

// US phone number formatting
function formatPhoneNumber(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
}

// Validate phone number (10 digits for US)
function isValidPhoneNumber(phone: string): boolean {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10;
}

interface SmsOptInPageProps {
  darkMode?: boolean;
}

export function SmsOptInPage({ darkMode = true }: SmsOptInPageProps) {
  const auth = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [currentStatus, setCurrentStatus] = useState<{
    isOptedIn: boolean;
    phoneNumber?: string;
    optedInAt?: string;
  } | null>(null);
  const [result, setResult] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Check current opt-in status on mount
  const checkStatus = useCallback(async () => {
    if (!auth.user?.email) return;

    setCheckingStatus(true);
    try {
      const status = await getSmsOptInStatus(auth.user.email);
      if (status.success) {
        setCurrentStatus({
          isOptedIn: status.isOptedIn,
          phoneNumber: status.phoneNumber,
          optedInAt: status.optedInAt,
        });
        if (status.phoneNumber) {
          setPhoneNumber(formatPhoneNumber(status.phoneNumber));
        }
      }
    } catch (err) {
      console.error('Failed to check opt-in status:', err);
    } finally {
      setCheckingStatus(false);
    }
  }, [auth.user?.email]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
    setResult(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!auth.user?.email) {
      setResult({ type: 'error', message: 'You must be signed in to opt in.' });
      return;
    }

    if (!isValidPhoneNumber(phoneNumber)) {
      setResult({ type: 'error', message: 'Please enter a valid 10-digit phone number.' });
      return;
    }

    if (!consent) {
      setResult({ type: 'error', message: 'You must agree to receive SMS notifications.' });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Extract just the digits for API
      const digits = phoneNumber.replace(/\D/g, '');
      const response = await submitSmsOptIn(digits, auth.user.email, true);

      if (response.success) {
        setResult({ type: 'success', message: response.message });
        setCurrentStatus({
          isOptedIn: true,
          phoneNumber: digits,
          optedInAt: new Date().toISOString(),
        });
      } else {
        setResult({ type: 'error', message: response.error || response.message });
      }
    } catch (err) {
      setResult({
        type: 'error',
        message: err instanceof Error ? err.message : 'An unexpected error occurred.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOptOut = async () => {
    if (!auth.user?.email) return;

    setLoading(true);
    setResult(null);

    try {
      const response = await submitSmsOptIn('', auth.user.email, false);

      if (response.success) {
        setResult({ type: 'success', message: 'You have been opted out of SMS notifications.' });
        setCurrentStatus({ isOptedIn: false });
        setPhoneNumber('');
        setConsent(false);
      } else {
        setResult({ type: 'error', message: response.error || response.message });
      }
    } catch (err) {
      setResult({
        type: 'error',
        message: err instanceof Error ? err.message : 'An unexpected error occurred.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''}`}>
      <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Link
                  to="/"
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </Link>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  SMS Notifications
                </h1>
              </div>
              {auth.user && (
                <div className="flex items-center gap-2">
                  {auth.user.picture && (
                    <img
                      src={auth.user.picture}
                      alt=""
                      className="w-8 h-8 rounded-full"
                    />
                  )}
                  <span className="text-sm text-gray-600 dark:text-gray-300 hidden sm:inline">
                    {auth.user.email}
                  </span>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-2">
              {currentStatus?.isOptedIn ? 'Manage SMS Notifications' : 'Opt In for SMS Notifications'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-center mb-8">
              {currentStatus?.isOptedIn
                ? 'You are currently receiving SMS notifications.'
                : 'Receive important updates and notifications via text message.'}
            </p>

            {checkingStatus ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : currentStatus?.isOptedIn ? (
              /* Already Opted In View */
              <div className="space-y-6">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-medium text-green-800 dark:text-green-200">
                        SMS Notifications Active
                      </p>
                      {currentStatus.phoneNumber && (
                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                          Phone: {formatPhoneNumber(currentStatus.phoneNumber)}
                        </p>
                      )}
                      {currentStatus.optedInAt && (
                        <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                          Opted in: {new Date(currentStatus.optedInAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleOptOut}
                  disabled={loading}
                  className="w-full px-4 py-3 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Opt Out of SMS Notifications'}
                </button>

                {result && (
                  <div className={`p-4 rounded-lg ${
                    result.type === 'success'
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                      : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                  }`}>
                    {result.message}
                  </div>
                )}
              </div>
            ) : (
              /* Opt In Form */
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Phone Number Input */}
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="(555) 555-5555"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    US phone numbers only. Standard message rates may apply.
                  </p>
                </div>

                {/* Consent Checkbox */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="consent"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    required
                  />
                  <label
                    htmlFor="consent"
                    className="text-sm text-gray-600 dark:text-gray-400"
                  >
                    I agree to receive SMS notifications from SSD Intranet. I understand that I can opt out at any time by returning to this page.
                  </label>
                </div>

                {/* Result Message */}
                {result && (
                  <div className={`p-4 rounded-lg ${
                    result.type === 'success'
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                      : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                  }`}>
                    {result.message}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || !consent || !isValidPhoneNumber(phoneNumber)}
                  className="w-full px-4 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    'Opt In for SMS Notifications'
                  )}
                </button>
              </form>
            )}

            {/* Privacy Note */}
            <p className="mt-8 text-xs text-gray-500 dark:text-gray-400 text-center">
              Your phone number will only be used for SSD Intranet notifications and will not be shared with third parties.
              <br />
              <Link to="/terms" className="text-blue-400 hover:text-blue-300">Terms and Conditions</Link>
              {' | '}
              <Link to="/privacy" className="text-blue-400 hover:text-blue-300">Privacy Policy</Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}

export default SmsOptInPage;
